import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Fixed presets so the Lost Leads Analysis report can cluster & chart cleanly.
// "Other" reveals a free-text field so nothing is lost when a nuance shows up.
export const LOSS_REASON_PRESETS = [
  'Price',
  'Switched insurer',
  'No response',
  'Chose competitor',
  'Policy no longer needed',
  'Other',
];

/**
 * Modal picker used everywhere we mark a lead or renewal as Lost.
 * Emits either one of the 6 presets or the free-text captured under "Other".
 *
 * Props:
 *   open       — controlled boolean
 *   title      — dialog title (e.g. "Mark Priya as Lost")
 *   description— context copy under the title
 *   confirming — disables the Confirm button while parent is saving
 *   onCancel   — () => void
 *   onConfirm  — (reason: string, notes: string | null) => void
 */
const LossReasonDialog = ({ open, title, description, confirming = false, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('Price');
  const [otherText, setOtherText] = useState('');
  const [notes, setNotes] = useState('');

  // Reset on close so the next open starts fresh (avoid leaking previous choice).
  useEffect(() => {
    if (!open) { setReason('Price'); setOtherText(''); setNotes(''); }
  }, [open]);

  const canConfirm = reason !== 'Other' || otherText.trim().length > 0;

  const handleConfirm = () => {
    const finalReason = reason === 'Other' ? otherText.trim() : reason;
    onConfirm(finalReason, notes.trim() || null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="loss-reason-dialog">
        <DialogHeader>
          <DialogTitle>{title || 'Why is this being marked as Lost?'}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Loss reason">
            {LOSS_REASON_PRESETS.map((p) => {
              const active = reason === p;
              return (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setReason(p)}
                  data-testid={`loss-reason-${p.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-left px-3 py-2 rounded-md border text-sm transition ${
                    active
                      ? 'border-red-500 bg-red-50 text-red-900 ring-1 ring-red-200'
                      : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <span className="inline-block h-2.5 w-2.5 rounded-full mr-2 align-middle" style={{ background: active ? '#DC2626' : '#CBD5E1' }} />
                  {p}
                </button>
              );
            })}
          </div>

          {reason === 'Other' && (
            <div>
              <Label className="text-xs">Custom reason *</Label>
              <Input
                autoFocus
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="e.g. moved to a different city"
                data-testid="loss-reason-other-input"
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Notes <span className="text-slate-400 font-normal">(optional, private)</span></Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything worth remembering next win-back attempt?"
              data-testid="loss-reason-notes-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel} disabled={confirming}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirm}
              disabled={!canConfirm || confirming}
              data-testid="loss-reason-confirm"
            >
              {confirming ? 'Saving…' : 'Mark as Lost'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LossReasonDialog;
